import { useUpdateUserSettings } from 'features/user';
import React, { useCallback } from 'react';
import { ResizableContainer } from 'shared/components/ui/resizable-container';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { PageEvents } from 'shared/services/tracking';
import { LimitKeys } from 'shared/types/limit-keys';
import { targetIsInput } from 'shared/utils/target-is-input';

import { GroupedList } from './components/grouped-list';
import { MobileAddTaskButton } from './components/mobile-add-task-button';
import { SidePanel } from './components/side-panel';
import * as Styled from './today.style';

const initialSidePanelWidth = 288;

export const Today: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.Today, undefined);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: isCalendarDayAllowed } = usePremiumFeatureAllowed(
    LimitKeys.DesktopCalendarDay,
    {},
  );

  const toggleSidePanelOpen = useCallback(
    () =>
      isCalendarDayAllowed
        ? updateUserSettings({
            today: { sidePanelOpen: !user?.settings?.today?.sidePanelOpen },
          })
        : openPremiumDialog(),
    [
      isCalendarDayAllowed,
      openPremiumDialog,
      updateUserSettings,
      user?.settings?.today?.sidePanelOpen,
    ],
  );

  const sidePanelOpen =
    isCalendarDayAllowed && !!user?.settings?.today?.sidePanelOpen;
  const setSidePanelWidth = useCallback(
    (width: number) => updateUserSettings({ today: { sidePanelWidth: width } }),
    [updateUserSettings],
  );

  useKeysDown(
    ['KeyC'],
    (e) => {
      if (targetIsInput(e.target) || !isCalendarDayAllowed) {
        return; // Don't open dialog
      }

      toggleSidePanelOpen();
    },
    {
      otherKeysInCombinationAllowed: true,
    },
  );

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <GroupedList
            onCalendar={toggleSidePanelOpen}
            calendarOpen={sidePanelOpen}
          />
        </Styled.MainContent>
      </Styled.Main>

      <Styled.SidePanel>
        <Styled.SidePanelContent $open={sidePanelOpen}>
          <ResizableContainer
            open={sidePanelOpen}
            resizeHandles="w"
            onWidthChange={setSidePanelWidth}
            initialWidth={
              user?.settings?.today?.sidePanelWidth ?? initialSidePanelWidth
            }
            smoothTransition
          >
            {sidePanelOpen && <SidePanel />}
          </ResizableContainer>
        </Styled.SidePanelContent>
      </Styled.SidePanel>

      <MobileAddTaskButton />
    </Styled.Container>
  );
};
